import React, { useState, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { useLanguage } from '../i18n';

const MODULATION_OPTIONS = [
  { id: 'deeper',  label: 'Deeper',  pitchShift: -5,   icon: '🔉' },
  { id: 'neutral', label: 'Neutral', pitchShift: -1.5, icon: '🎭' },
  { id: 'higher',  label: 'Higher',  pitchShift: 4,    icon: '🔊' },
];

const MAX_DURATION = 30;
const UPLOAD_TIMEOUT_MS = 30000;
const SILENCE_THRESHOLD = 0.001; // peak amplitude below this is treated as "no real audio"

function audioBufferToWav(buffer) {
  const numChannels = buffer.numberOfChannels;
  const sampleRate  = buffer.sampleRate;
  const bitDepth    = 16;
  const dataLength  = buffer.length * numChannels * (bitDepth / 8);
  const arrayBuf    = new ArrayBuffer(44 + dataLength);
  const view        = new DataView(arrayBuf);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0,  'RIFF');
  view.setUint32(4,  36 + dataLength, true);
  writeString(8,  'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1,           true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate,  true);
  view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
  view.setUint16(32, numChannels * (bitDepth / 8), true);
  view.setUint16(34, bitDepth,    true);
  writeString(36, 'data');
  view.setUint32(40, dataLength,  true);

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuf], { type: 'audio/wav' });
}

// Returns the loudest single sample (0 = total silence, 1 = full scale).
// Used to catch a recording or a render that "succeeded" technically but
// contains no actual sound — the exact failure mode that used to slip
// through and produce normal-looking, unplayable voice notes.
function getPeakAmplitude(audioBuffer) {
  let peak = 0;
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
    const data = audioBuffer.getChannelData(ch);
    for (let i = 0; i < data.length; i++) {
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }
  }
  return peak;
}

export default function VoiceNote({ user, userData, chatId, setUserData, veylId, partnerVeylId, groupMode, groupVoiceLeft, theme, onSent, onLimitReached }) {
  const { t } = useLanguage();
  const [recordingTime, setRecordingTime]   = useState(0);
  const [audioBlob, setAudioBlob]           = useState(null);
  const [modulation, setModulation]         = useState('neutral');
  const [isSending, setIsSending]           = useState(false);
  const [isProcessing, setIsProcessing]     = useState(false);
  const [previewUrl, setPreviewUrl]         = useState(null);
  const [isPlaying, setIsPlaying]           = useState(false);
  const [error, setError]                   = useState(null);
  const [panel, setPanel]                   = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const timerRef         = useRef(null);
  const previewAudioRef  = useRef(null);
  const modulatedBlobRef = useRef(null);
  const uploadTaskRef    = useRef(null);

  const dark = theme === 'dark';

  const isPremium      = userData?.isPremium;
  const voiceNotesUsed = userData?.activeVeyl?.voiceNotesUsedToday || 0;
  const chatNotesLeft  = isPremium ? Infinity : (10 - voiceNotesUsed);
  const notesLeft      = groupMode ? (groupVoiceLeft ?? Infinity) : chatNotesLeft;

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (uploadTaskRef.current) uploadTaskRef.current.cancel();
    };
  }, []);

  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4',
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return '';
  };

  const startRecording = async () => {
    setError(null);
    if (!isPremium && notesLeft <= 0) {
      if (onLimitReached) onLimitReached();
      return;
    }
    try {
      const stream   = await navigator.mediaDevices.getUserMedia({ audio: true });

      const audioTrack = stream.getAudioTracks()[0];
      console.log('[VoiceNote] mic track acquired:', {
        label: audioTrack?.label,
        enabled: audioTrack?.enabled,
        muted: audioTrack?.muted,
        readyState: audioTrack?.readyState,
        settings: audioTrack?.getSettings ? audioTrack.getSettings() : null,
      });

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const mimeUsed = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeUsed });
        setAudioBlob(blob);
        setPanel('preview');
        await applyModulation(blob, 'neutral');
      };

      mediaRecorder.start(100);
      setRecordingTime(0);
      setPanel('recording');

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= MAX_DURATION) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      setError(t('micDenied'));
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const applyModulation = async (blob, modulationId) => {
    setIsProcessing(true);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);

    try {
      const option     = MODULATION_OPTIONS.find(o => o.id === modulationId);
      const pitchShift = option?.pitchShift ?? -1.5;
      const rate       = Math.pow(2, pitchShift / 12);

      const arrayBuffer = await blob.arrayBuffer();
      const audioCtx    = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      await audioCtx.close();

      // Catch a dead mic capture before we waste time pitch-shifting it.
      const rawPeak = getPeakAmplitude(audioBuffer);
      console.log('[VoiceNote] raw capture — peak:', rawPeak.toFixed(4), 'duration:', audioBuffer.duration.toFixed(2) + 's');
      if (rawPeak < SILENCE_THRESHOLD) {
        throw new Error('SILENT_SOURCE');
      }

      const sampleRate   = audioBuffer.sampleRate;
      const outputLength = Math.ceil(audioBuffer.length / rate);

      const offlineCtx = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        outputLength,
        sampleRate
      );

      const source = offlineCtx.createBufferSource();
      source.buffer             = audioBuffer;
      source.playbackRate.value = rate;
      source.connect(offlineCtx.destination);
      source.start();

      const rendered = await offlineCtx.startRendering();

      // Catch a render that "succeeded" but produced no actual sound —
      // this is the check that was missing before, and the most likely
      // explanation for a normal-looking file that plays nothing.
      const renderedPeak = getPeakAmplitude(rendered);
      console.log('[VoiceNote] rendered output — peak:', renderedPeak.toFixed(4), 'duration:', rendered.duration.toFixed(2) + 's');
      if (renderedPeak < SILENCE_THRESHOLD) {
        throw new Error('SILENT_RENDER');
      }

      const wavBlob = audioBufferToWav(rendered);
      modulatedBlobRef.current = wavBlob;
      setPreviewUrl(URL.createObjectURL(wavBlob));

    } catch (err) {
      console.error('[VoiceNote] modulation failed:', err.name, err.message || err);
      // Deliberately NOT falling back to uploading the raw, unprocessed
      // recording anymore. That fallback used to silently upload the
      // original webm/ogg blob under a .wav filename and audio/wav
      // content-type — producing a file that looks completely normal in
      // Storage (right size, right-looking metadata) but that no player
      // can actually decode as WAV. Blocking here and surfacing an error
      // is safer than shipping a dead file.
      modulatedBlobRef.current = null;
      setPreviewUrl(null);

      // TEMPORARY DIAGNOSTIC: shows the real reason on screen.
      // Remove the `reason` part once the cause is found.
      let reason = 'decode/render error: ' + (err.name || '') + ' ' + (err.message || '');
      if (err.message === 'SILENT_SOURCE') reason = 'mic captured silence';
      if (err.message === 'SILENT_RENDER') reason = 'render came out silent';
      setError(`${t('modulationFailed')} [${reason}]`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModulationChange = async (newMod) => {
    if (isProcessing) return;
    setModulation(newMod);
    setIsPlaying(false);
    if (previewAudioRef.current) previewAudioRef.current.pause();
    if (audioBlob) await applyModulation(audioBlob, newMod);
  };

  const togglePreviewPlay = () => {
    if (!previewAudioRef.current || isProcessing || !previewUrl) return;
    if (isPlaying) {
      previewAudioRef.current.pause();
      setIsPlaying(false);
    } else {
      previewAudioRef.current.play();
      setIsPlaying(true);
    }
  };

  const sendVoiceNote = async () => {
    const blobToSend = modulatedBlobRef.current;
    if (!blobToSend || blobToSend.size === 0) {
      setError(t('modulationFailed'));
      return;
    }
    setIsSending(true);
    setError(null);
    setUploadProgress(0);

    try {
      const timestamp = Date.now();
      let storagePath;

      if (groupMode) {
        storagePath = `group_voice_notes/${chatId}/${user.uid}/${user.uid}_${timestamp}.wav`;
      } else {
        const partnerId = userData?.activeVeyl?.partnerId;
        const partnerPair = [user.uid, partnerId].sort().join('_');
        storagePath = `voice_notes/${partnerPair}/${user.uid}_${timestamp}.wav`;
      }

      const storageRef = ref(storage, storagePath);

      const downloadURL = await new Promise((resolve, reject) => {
        const task = uploadBytesResumable(storageRef, blobToSend, {
          contentType: 'audio/wav',
          customMetadata: {
            senderId:   user.uid,
            senderName: userData.name,
            chatId,
            modulation,
          }
        });

        uploadTaskRef.current = task;

        const timeout = setTimeout(() => {
          task.cancel();
          reject(new Error('Upload timed out. Check your Storage rules or internet connection.'));
        }, UPLOAD_TIMEOUT_MS);

        task.on(
          'state_changed',
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(pct);
          },
          (err) => {
            clearTimeout(timeout);
            reject(err);
          },
          async () => {
            clearTimeout(timeout);
            try {
              const url = await getDownloadURL(task.snapshot.ref);
              resolve(url);
            } catch (e) {
              reject(e);
            }
          }
        );
      });

      // Message write + counter increment happen server-side now, via the
      // sendVoiceNote callable. firestore.rules' isNotTamperingWithCounters()
      // only allows a client write to voiceNotesUsedToday when it's being
      // reset to 0 — this component used to try to increment it directly,
      // which was always rejected with "Missing or insufficient permissions"
      // right after a successful upload, and the counter never advanced.
      const functionsInstance = getFunctions();
      const sendVoiceNoteFn   = httpsCallable(functionsInstance, 'sendVoiceNote');

      const payload = groupMode
        ? { mode: 'group', groupId: chatId, audioUrl: downloadURL, storagePath, duration: recordingTime, modulation }
        : { mode: 'chat', veylId, partnerVeylId, audioUrl: downloadURL, storagePath, duration: recordingTime, modulation };

      await sendVoiceNoteFn(payload);

      // Optimistic local bump for instant UI feedback — the live veyl/user
      // listener upstream reconciles this with the real server value right
      // after the transaction commits.
      if (!isPremium && !groupMode && veylId) {
        setUserData(prev => ({
          ...prev,
          activeVeyl: {
            ...prev.activeVeyl,
            voiceNotesUsedToday: (prev?.activeVeyl?.voiceNotesUsedToday || 0) + 1
          }
        }));
      }

      resetPanel();
      if (onSent) onSent();

    } catch (err) {
      setError(err.message || t('failedToSend'));
    } finally {
      setIsSending(false);
      setUploadProgress(0);
      uploadTaskRef.current = null;
    }
  };

  const resetPanel = () => {
    setPanel('idle');
    setAudioBlob(null);
    setRecordingTime(0);
    setIsPlaying(false);
    setError(null);
    setUploadProgress(0);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setModulation('neutral');
    modulatedBlobRef.current = null;
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const panelBg        = dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const panelBorder    = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const idleBtnBorder  = dark ? 'rgba(255,255,255,0.1)'  : '#d1d5db';
  const recordingText  = dark ? 'rgba(255,255,255,0.85)' : '#1B2D44';
  const processingText = dark ? 'rgba(255,255,255,0.4)'  : 'rgba(0,0,0,0.4)';
  const durationText   = dark ? 'rgba(255,255,255,0.4)'  : 'rgba(0,0,0,0.4)';
  const progressTrack  = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const uploadText     = dark ? 'rgba(255,255,255,0.3)'  : 'rgba(0,0,0,0.35)';
  const limitText      = dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.35)';
  const modUnselectedColor  = dark ? 'rgba(255,255,255,0.5)'  : 'rgba(0,0,0,0.65)';
  const modUnselectedBorder = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.2)';
  const modUnselectedBg     = dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)';

  const dynamicPanelStyle = {
    border: `1px solid ${panelBorder}`,
    borderRadius: '0.875rem',
    padding: '0.875rem',
    backgroundColor: panelBg,
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '0.5rem'
  };

  const dynamicSecondaryBtn = {
    padding: '0.5rem 1.25rem',
    backgroundColor: dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
    color: dark ? 'rgba(255,255,255,0.6)' : '#4b5563',
    border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : '#d1d5db'}`,
    borderRadius: '0.5rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '0.85rem'
  };

  if (panel === 'idle') {
    return (
      <button
        onClick={startRecording}
        disabled={!isPremium && notesLeft <= 0}
        title={!isPremium && notesLeft <= 0 ? t('dailyLimitReachedVoice') : t('recordVoiceNote')}
        style={{
          padding: '0.75rem',
          backgroundColor: (!isPremium && notesLeft <= 0)
            ? (dark ? 'rgba(255,255,255,0.03)' : '#f3f4f6')
            : (dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6'),
          border: `1px solid ${idleBtnBorder}`,
          borderRadius: '0.75rem',
          cursor: (!isPremium && notesLeft <= 0) ? 'not-allowed' : 'pointer',
          fontSize: '1.25rem',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: (!isPremium && notesLeft <= 0) ? 0.4 : 1
        }}
      >
        🎤
      </button>
    );
  }

  if (panel === 'recording') {
    const progress = (recordingTime / MAX_DURATION) * 100;
    return (
      <div style={dynamicPanelStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.25rem', animation: 'pulse 1s infinite' }}>🔴</span>
          <span style={{ fontWeight: '600', color: recordingText, fontSize: '0.9rem' }}>
            {t('recording')}
          </span>
          <span style={{
            marginLeft: 'auto', fontWeight: '700',
            color: recordingTime >= MAX_DURATION - 5 ? '#f87171' : '#008081',
            fontVariantNumeric: 'tabular-nums', fontSize: '0.875rem'
          }}>
            {formatTime(recordingTime)} / {formatTime(MAX_DURATION)}
          </span>
        </div>
        <div style={{ height: '4px', backgroundColor: progressTrack, borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${progress}%`,
            backgroundColor: recordingTime >= MAX_DURATION - 5 ? '#f87171' : '#008081',
            transition: 'width 1s linear', borderRadius: '2px'
          }} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={resetPanel} style={dynamicSecondaryBtn}>{t('cancel')}</button>
          <button onClick={stopRecording} style={primaryBtn}>⏹ {t('done')}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={dynamicPanelStyle}>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {MODULATION_OPTIONS.map(opt => (
          <button
            key={opt.id}
            onClick={() => handleModulationChange(opt.id)}
            disabled={isProcessing}
            style={{
              padding: '0.4rem 0.85rem',
              borderRadius: '9999px',
              border: '1.5px solid',
              borderColor:     modulation === opt.id ? '#008081' : modUnselectedBorder,
              backgroundColor: modulation === opt.id ? 'rgba(0,128,129,0.15)' : modUnselectedBg,
              color:           modulation === opt.id ? '#008081' : modUnselectedColor,
              fontWeight:      modulation === opt.id ? '700' : '500',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '0.8rem',
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              transition: 'all 0.15s'
            }}
          >
            {opt.icon} {t(opt.id)}
          </button>
        ))}
      </div>

      {isProcessing ? (
        <div style={{ textAlign: 'center', color: processingText, fontSize: '0.85rem', padding: '0.5rem 0' }}>
          {t('applyingModulation')}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={togglePreviewPlay} disabled={!previewUrl} style={{ ...primaryBtn, padding: '0.5rem 1rem', opacity: previewUrl ? 1 : 0.5 }}>
            {isPlaying ? `⏸ ${t('pause')}` : `▶ ▶ ${t('preview')}`}
          </button>
          <span style={{ fontSize: '0.8rem', color: durationText, fontVariantNumeric: 'tabular-nums' }}>
            {formatTime(recordingTime)}
          </span>
          <audio
            ref={previewAudioRef}
            src={previewUrl}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {isSending && uploadProgress > 0 && (
        <div>
          <div style={{ height: '3px', backgroundColor: progressTrack, borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${uploadProgress}%`,
              background: 'linear-gradient(90deg, #008081, #4B2C5E)',
              transition: 'width 0.3s ease', borderRadius: '2px'
            }} />
          </div>
          <div style={{ fontSize: '0.7rem', color: uploadText, textAlign: 'center', marginTop: '0.25rem' }}>
            {t('uploading')} {uploadProgress}%
          </div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: '0.8rem', color: '#f87171', textAlign: 'center' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button onClick={resetPanel} style={dynamicSecondaryBtn}>{t('discard')}</button>
        <button
          onClick={sendVoiceNote}
          disabled={isSending || isProcessing || !modulatedBlobRef.current}
          style={{ ...primaryBtn, opacity: (isSending || isProcessing || !modulatedBlobRef.current) ? 0.5 : 1 }}
        >
          {isSending ? t('sending') : `📤 ${t('send')}`}
        </button>
      </div>

      {!isPremium && !groupMode && (
        <div style={{ fontSize: '0.7rem', color: limitText, textAlign: 'center' }}>
          {notesLeft > 0
            ? t('voiceNotesLeft', { count: notesLeft, plural: notesLeft !== 1 ? 's' : '' })
            : t('dailyLimitReachedVoice')
          } · {t('maxDuration', { seconds: 30 })}
        </div>
      )}
    </div>
  );
}

const primaryBtn = {
  padding: '0.5rem 1.25rem',
  background: 'linear-gradient(135deg, #008081, #005f5f)',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '0.5rem',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '0.85rem',
  boxShadow: '0 2px 8px rgba(0,128,129,0.3)'
};